// Тесният списък „Продукт · Наличност" вляво от складовата карта (≥ 1024 px):
// докато картата е отворена, той заема мястото на таблицата. Изборът СЛЕДВА
// фокуса — ↑/↓ (и Home/End) сменят продукта в картата, Enter/→ влиза в
// картата, Esc я затваря (глобално, `useStockCardSelection`). Редовете са
// същите като в таблицата (филтрирани, сортирани, до „Покажи още").
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { formatQty } from "../../components/qty";
import { KbdChip } from "../../components/KbdChip";
import { DeletedProductChip } from "../../components/product/DeletedProductChip";
import type { Period } from "../../components/period/periodPresets";
import { ROW_READ } from "../../components/list/density";
import { REPORT_HEAD_CLASS, REPORT_HEADER_ROW_CLASS } from "../components/reportListModel";
import type { StockRow } from "../components/stockShared";
import { StockSubstitutionBadge } from "./StockSubstitutionBadge";
import type { ProductSubstitutionSummary } from "./stockSubstitutionModel";

type Props = {
  rows: StockRow[];
  selectedId: number;
  onSelect: (row: StockRow) => void;
  onEnterCard: () => void;
  substitutions: Map<number, ProductSubstitutionSummary>;
  substitutionPeriod: Period;
  footer?: ReactNode;
};

export const StockCardMasterList = ({
  rows,
  selectedId,
  onSelect,
  onEnterCard,
  substitutions,
  substitutionPeriod,
  footer,
}: Props) => {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement | null>(null);

  const optionOf = (productId: number) =>
    listRef.current?.querySelector<HTMLElement>(`[data-card-product="${productId}"]`) ?? null;

  // При отваряне фокусът идва тук (от реда на таблицата, която се скрива) —
  // иначе ↑/↓ веднага след Enter нямаше да сменят продукта.
  useEffect(() => {
    optionOf(selectedId)?.focus({ preventScroll: false });
    // само при монтиране: после фокусът се мести от самите клавиши
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const move = (index: number) => {
    const target = rows[index];
    if (!target) return;
    onSelect(target);
    const option = optionOf(target.productId);
    option?.focus();
    option?.scrollIntoView?.({ block: "nearest" });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const index = rows.findIndex((row) => row.productId === selectedId);
    let next: number | null = null;
    if (event.key === "ArrowDown") next = Math.min(index + 1, rows.length - 1);
    else if (event.key === "ArrowUp") next = Math.max(index - 1, 0);
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = rows.length - 1;
    else if (event.key === "Enter" || event.key === "ArrowRight") {
      event.preventDefault();
      onEnterCard();
      return;
    }
    if (next == null) return;
    event.preventDefault();
    move(next);
  };

  return (
    <div className="@container flex flex-col">
      <div className={`flex items-center gap-3 px-3.5 ${REPORT_HEADER_ROW_CLASS}`}>
        <span className={`${REPORT_HEAD_CLASS} flex-1`}>{t("inventoryPage.colProductName")}</span>
        <span className="hidden items-center gap-1 text-[11.5px] leading-[normal] text-muted @min-[440px]:inline-flex">
          <KbdChip>↑</KbdChip>
          <KbdChip>↓</KbdChip>
          {t("inventoryPage.scKeysSwitch")}
          <KbdChip>Esc</KbdChip>
          {t("inventoryPage.scKeysClose")}
        </span>
        <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.colAvailable")}</span>
      </div>
      <div
        ref={listRef}
        role="listbox"
        aria-label={t("inventoryPage.scMasterListAria")}
        onKeyDown={onKeyDown}
      >
        {rows.map((row) => {
          const active = row.productId === selectedId;
          return (
            <div
              key={row.productId}
              role="option"
              aria-selected={active}
              data-card-product={row.productId}
              tabIndex={active ? 0 : -1}
              onClick={() => onSelect(row)}
              className={`flex cursor-pointer items-center gap-2.5 border-l-[3px] border-t border-t-shade-1 pl-[11px] pr-3.5 ${ROW_READ} outline-none focus-visible:bg-fill ${
                active ? "border-l-brand bg-brand-tint" : "border-l-transparent hover:bg-fill"
              }`}
            >
              <span
                className={`min-w-0 flex-1 truncate text-[13.5px] leading-[1.3] ${
                  active ? "font-semibold text-ink" : "text-ink-2"
                }`}
              >
                {row.name}
              </span>
              {row.deleted && <DeletedProductChip />}
              <StockSubstitutionBadge
                summary={substitutions.get(row.productId)}
                period={substitutionPeriod}
                decimals={row.decimals}
                compact
              />
              <span
                className={`shrink-0 whitespace-nowrap font-sheet text-[13px] tabular-nums ${
                  row.bucket === "under" && row.minQty ? "font-semibold text-danger-ink" : "text-ink-2"
                }`}
              >
                {formatQty(row.quantity, row.decimals)}
                <span className="ml-1 font-normal text-muted">{row.unit ?? ""}</span>
              </span>
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
};

export default StockCardMasterList;
